import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Check, Clock, ArrowRight, Users, GraduationCap, MapPin, ChevronDown,
  Award, BookOpen, Star, Calendar, Wifi, Monitor, Layers, Package,
  FileText, AlertCircle, UserCheck, ClipboardList, ChevronRight,
} from "lucide-react";
import api, { BACKEND } from "@/lib/api";
import { useCms } from "@/lib/cms";

// ── Helpers ───────────────────────────────────────────────────────────────────
const resolveImg = (url) =>
  !url ? null : url.startsWith("http") ? url : `${BACKEND}${url}`;

// Mode → icon mapping — fully dynamic, no hardcoded fallback text
const MODE_ICON = {
  Online:  Wifi,
  Offline: MapPin,
  Hybrid:  Layers,
  "On-site": MapPin,
  Blended: Layers,
};
const modeIcon = (mode) => {
  if (!mode) return Users;
  return MODE_ICON[mode] || Users;
};

// ── Expandable FAQ ────────────────────────────────────────────────────────────
function FaqItem({ faq }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-start justify-between gap-3 px-5 py-4 text-left hover:bg-gray-50 transition-colors"
      >
        <span className="text-sm font-medium text-[#0F172A] leading-snug">{faq.question}</span>
        <ChevronDown className={`w-4 h-4 text-gray-400 shrink-0 mt-0.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 pb-4 text-sm text-gray-600 leading-relaxed border-t border-gray-100">
          {faq.answer}
        </div>
      )}
    </div>
  );
}

// ── Section heading ───────────────────────────────────────────────────────────
function SectionTitle({ children }) {
  return (
    <h2 className="font-display text-xl sm:text-2xl font-semibold mb-3 text-[#0F172A]">
      {children}
    </h2>
  );
}

// ── Checklist item ────────────────────────────────────────────────────────────
function CheckItem({ text, color = "text-emerald-600" }) {
  return (
    <li className="flex items-start gap-2 text-sm text-gray-700 leading-snug">
      <Check className={`w-4 h-4 ${color} mt-0.5 shrink-0`} />
      {text}
    </li>
  );
}

// ── Meta pill ─────────────────────────────────────────────────────────────────
function MetaPill({ icon: Icon, label, color = "text-gray-400" }) {
  if (!label) return null;
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-100 text-sm text-gray-700 whitespace-nowrap">
      <Icon className={`w-3.5 h-3.5 ${color} shrink-0`} />
      {label}
    </span>
  );
}

// ── Stat box ──────────────────────────────────────────────────────────────────
function StatBox({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 text-center">
      <div className="text-xl font-bold text-[#0F172A]">{value}</div>
      <div className="text-xs text-gray-500 mt-0.5">{label}</div>
    </div>
  );
}

// ── CTA card ─────────────────────────────────────────────────────────────────
function CtaCard({ p }) {
  const heading     = p.cta_heading     || "Bring this to your school";
  const subheading  = p.cta_subheading  || "Custom cohort";
  const description = p.cta_description || "Delivered on your dates. Certificate for every teacher.";
  const bookText    = p.cta_book_text   || "Book this training";
  const feedbackText = p.cta_feedback_text || "Submit Feedback & Claim Certificate";
  const notice      = p.cta_notice      || "Certificate is issued after admin approval and emailed to you.";
  const showBook     = p.cta_book_show !== false;
  const showFeedback = p.cta_feedback_show !== false;

  return (
    <div className="rounded-2xl overflow-hidden border border-gray-200 bg-white">
      <div className="p-5 sm:p-6">
        {/* Info rows inside sidebar card */}
        <div className="space-y-2 mb-5 text-sm">
          {p.duration_hours > 0 && (
            <div className="flex items-center gap-2 text-gray-600">
              <Clock className="w-4 h-4 text-gray-400 shrink-0" />
              <span>{p.duration_hours} hours</span>
            </div>
          )}
          {p.mode && (() => { const MIcon = modeIcon(p.mode); return (
            <div className="flex items-center gap-2 text-gray-600">
              <MIcon className="w-4 h-4 text-gray-400 shrink-0" />
              <span>{p.mode}</span>
            </div>
          ); })()}
          {p.level && (
            <div className="flex items-center gap-2 text-gray-600">
              <GraduationCap className="w-4 h-4 text-gray-400 shrink-0" />
              <span>{p.level}</span>
            </div>
          )}
          {p.certificate_type && (
            <div className="flex items-center gap-2 text-gray-600">
              <Award className="w-4 h-4 text-gray-400 shrink-0" />
              <span>{p.certificate_type}</span>
            </div>
          )}
          {p.handouts && (
            <div className="flex items-center gap-2 text-gray-600">
              <Package className="w-4 h-4 text-gray-400 shrink-0" />
              <span>{p.handouts}</span>
            </div>
          )}
          {p.venue && (
            <div className="flex items-center gap-2 text-gray-600">
              <MapPin className="w-4 h-4 text-gray-400 shrink-0" />
              <span>{p.venue}</span>
            </div>
          )}
          {p.start_date && (
            <div className="flex items-center gap-2 text-gray-600">
              <Calendar className="w-4 h-4 text-gray-400 shrink-0" />
              <span>
                {p.start_date}
                {p.end_date && p.end_date !== p.start_date ? ` – ${p.end_date}` : ""}
              </span>
            </div>
          )}
          {p.max_participants && (
            <div className="flex items-center gap-2 text-gray-600">
              <Users className="w-4 h-4 text-gray-400 shrink-0" />
              <span>Max {p.max_participants} participants</span>
            </div>
          )}
          {p.available_seats != null && p.available_seats > 0 && (
            <div className="flex items-center gap-2 text-gray-600">
              <UserCheck className="w-4 h-4 text-gray-400 shrink-0" />
              <span>{p.available_seats} seats available</span>
            </div>
          )}
          {(p.price > 0 || p.discount > 0) && (
            <div className="flex items-center gap-2 text-gray-700 font-medium">
              <span className="w-4 h-4 text-gray-400 shrink-0 grid place-items-center text-xs">₹</span>
              {p.discount > 0
                ? <><span className="line-through text-gray-400 font-normal">₹{p.price}</span><span className="ml-1 text-emerald-600">₹{p.price - p.discount}</span></>
                : <span>₹{p.price}</span>
              }
            </div>
          )}
        </div>

        {/* CTA heading / label */}
        <div className="border-t border-gray-100 pt-4">
          <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold">{heading}</div>
          <div className="mt-1.5 font-display text-xl sm:text-2xl font-semibold leading-snug text-[#0F172A]">{subheading}</div>
          <div className="mt-1 text-sm text-gray-600 leading-relaxed">{description}</div>

          {showBook && (
            <Link
              to={`/book?program=${p.id}`}
              data-testid="program-book-cta"
              className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#0F172A] hover:bg-[#1e293b] text-white text-sm font-semibold px-4 py-3 rounded-xl transition-colors"
            >
              {bookText} <ArrowRight className="w-4 h-4 shrink-0" />
            </Link>
          )}

          {showFeedback && (
            <>
              <div className="my-4 flex items-center gap-3">
                <div className="flex-1 h-px bg-gray-100" />
                <div className="text-[10px] font-mono uppercase tracking-wider text-gray-400 whitespace-nowrap">Attended already?</div>
                <div className="flex-1 h-px bg-gray-100" />
              </div>
              <Link
                to={`/feedback/${p.id}?title=${encodeURIComponent(p.title)}`}
                data-testid="program-feedback-cta"
                className="w-full inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-4 py-3 rounded-xl shadow-sm shadow-emerald-900/10 transition-colors"
              >
                {feedbackText} <ArrowRight className="w-4 h-4 shrink-0" />
              </Link>
              <div className="mt-2.5 text-[11px] text-gray-500 text-center leading-snug">
                {notice}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function ProgramDetails() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const { site } = useCms();

  useEffect(() => {
    api.get(`/programs/${id}`).then((r) => setP(r.data)).catch(() => {});
  }, [id]);

  // SEO — update document title from CMS data (no helmet dependency)
  useEffect(() => {
    if (!p) return;
    const title = p.seo_title || p.title;
    if (title) document.title = title;
    // Meta description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.name = "description";
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = p.seo_description || p.subtitle || p.description?.slice(0, 160) || "";
    return () => { document.title = site?.company_name || ""; };
  }, [p]);

  if (!p) return (
    <div className="max-w-3xl mx-auto p-16 text-center text-gray-500">Loading…</div>
  );

  // Image positioning
  const coverSrc = resolveImg(p.cover_image);
  const bannerSrc = resolveImg(p.banner_image);
  const offsetX = p.image_offset_x ?? 0;
  const offsetY = p.image_offset_y ?? 0;
  const scale   = (p.image_scale ?? 100) / 100;
  const imgStyle = {
    objectPosition: `${50 + offsetX / 2}% ${50 + offsetY / 2}%`,
    transform: `scale(${scale})`,
    transformOrigin: "center",
  };

  // Mode icon
  const ModeIcon = modeIcon(p.mode);

  // Registration status
  const regOpen = p.registration_enabled !== false;

  return (
    <div className="bg-white min-h-screen">

      {/* ── Hero banner (if banner_image set) ── */}
      {bannerSrc && (
        <div className="relative w-full h-48 sm:h-64 lg:h-72 overflow-hidden bg-gray-900">
          <img
            src={bannerSrc}
            alt={p.title}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-indigo-300 mb-1">
              {p.category}
            </div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold text-white leading-tight line-clamp-2">
              {p.title}
            </h1>
          </div>
        </div>
      )}

      {/* ── Breadcrumb ── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-5 text-xs sm:text-sm text-gray-400 flex items-center gap-1.5 flex-wrap min-w-0">
        <Link to="/programs" className="hover:text-[#4F46E5] shrink-0">Programs</Link>
        <ChevronRight className="w-3 h-3 shrink-0" />
        <span className="text-[#0F172A] truncate">{p.title}</span>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-24 grid lg:grid-cols-12 gap-8 lg:gap-12 items-start">

        {/* ── LEFT / MAIN COLUMN ── */}
        <div className="lg:col-span-7 min-w-0">

          {/* Category + badges — only shown when no banner (banner already shows category) */}
          {!bannerSrc && (
            <div className="flex flex-wrap items-center gap-2 mb-3">
              {p.category && (
                <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#4F46E5]">
                  {p.category}
                </span>
              )}
              {p.featured && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                  <Star className="w-3 h-3 fill-amber-500" /> Featured
                </span>
              )}
              {!regOpen && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200">
                  Registrations closed
                </span>
              )}
            </div>
          )}

          {/* Title — only shown when no banner */}
          {!bannerSrc && (
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-semibold leading-[1.1] text-[#0F172A]">
              {p.title}
            </h1>
          )}

          {p.subtitle && (
            <p className="mt-3 text-base sm:text-lg text-gray-600 leading-relaxed">
              {p.subtitle}
            </p>
          )}

          {/* Meta pills — all dynamic */}
          <div className="mt-5 flex flex-wrap gap-2 sm:gap-3">
            {p.duration_hours > 0 && <MetaPill icon={Clock} label={`${p.duration_hours} hours`} />}
            {p.level && <MetaPill icon={GraduationCap} label={p.level} />}
            {p.mode && <MetaPill icon={ModeIcon} label={p.mode} />}
            {p.certificate_type && <MetaPill icon={Award} label={p.certificate_type} color="text-emerald-500" />}
            {p.handouts && <MetaPill icon={Package} label={p.handouts} color="text-indigo-400" />}
            {p.venue && <MetaPill icon={MapPin} label={p.venue} />}
            {p.start_date && (
              <MetaPill icon={Calendar} label={
                p.end_date && p.end_date !== p.start_date
                  ? `${p.start_date} – ${p.end_date}`
                  : p.start_date
              } />
            )}
            {!regOpen && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200 text-sm font-medium">
                <AlertCircle className="w-3.5 h-3.5 shrink-0" /> Registrations closed
              </span>
            )}
          </div>

          {/* Stats row */}
          {(p.max_participants || p.available_seats != null) && (
            <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-3">
              {p.max_participants && <StatBox label="Max participants" value={p.max_participants} />}
              {p.available_seats != null && p.available_seats >= 0 && (
                <StatBox label="Seats available" value={p.available_seats} />
              )}
              {p.duration_hours > 0 && <StatBox label="Training hours" value={`${p.duration_hours}h`} />}
            </div>
          )}

          {/* Cover image — mobile only */}
          {coverSrc && (
            <div className="mt-6 rounded-2xl overflow-hidden aspect-[16/10] bg-gray-100 lg:hidden">
              <img
                src={coverSrc}
                alt={p.title}
                loading="lazy"
                className="w-full h-full object-cover"
                style={imgStyle}
              />
            </div>
          )}

          {/* ── About ── */}
          {(p.description || p.full_description) && (
            <div className="mt-8">
              <SectionTitle>About this program</SectionTitle>
              {p.description && (
                <p className="text-gray-700 leading-relaxed text-sm sm:text-base">{p.description}</p>
              )}
              {p.full_description && (
                <p className="mt-3 text-gray-600 leading-relaxed text-sm sm:text-base">{p.full_description}</p>
              )}
            </div>
          )}

          {/* ── Trainer ── */}
          {p.trainer && (
            <div className="mt-6 rounded-2xl border border-gray-200 bg-gray-50 p-4 sm:p-5">
              <div className="flex items-start gap-3">
                <UserCheck className="w-5 h-5 text-indigo-500 shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <div className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-0.5">Trainer</div>
                  <p className="text-sm font-medium text-[#0F172A]">{p.trainer}</p>
                  {p.faculty_details && (
                    <p className="mt-1 text-sm text-gray-500 leading-relaxed">{p.faculty_details}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── Highlights + Outcomes ── */}
          {((p.highlights?.length > 0) || (p.outcomes?.length > 0)) && (
            <div className="mt-8 grid sm:grid-cols-2 gap-6 sm:gap-8">
              {p.highlights?.length > 0 && (
                <div>
                  <h3 className="font-display text-lg font-semibold mb-3 text-[#0F172A]">Highlights</h3>
                  <ul className="space-y-2.5">
                    {p.highlights.map((h, i) => <CheckItem key={i} text={h} color="text-emerald-600" />)}
                  </ul>
                </div>
              )}
              {p.outcomes?.length > 0 && (
                <div>
                  <h3 className="font-display text-lg font-semibold mb-3 text-[#0F172A]">Learning Outcomes</h3>
                  <ul className="space-y-2.5">
                    {p.outcomes.map((h, i) => <CheckItem key={i} text={h} color="text-[#4F46E5]" />)}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* ── Learning Objectives ── */}
          {p.learning_objectives?.length > 0 && (
            <div className="mt-8">
              <h3 className="font-display text-lg font-semibold mb-3 text-[#0F172A]">Learning Objectives</h3>
              <ul className="space-y-2.5">
                {p.learning_objectives.map((o, i) => <CheckItem key={i} text={o} color="text-indigo-400" />)}
              </ul>
            </div>
          )}

          {/* ── Training Modules ── */}
          {p.training_modules?.length > 0 && (
            <div className="mt-8">
              <h3 className="font-display text-lg font-semibold mb-3 text-[#0F172A]">Training Modules</h3>
              <ol className="space-y-2">
                {p.training_modules.map((m, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-gray-700">
                    <span className="w-5 h-5 rounded-full bg-[#EEF2FF] text-[#4F46E5] text-[10px] font-bold grid place-items-center shrink-0 mt-0.5">
                      {i + 1}
                    </span>
                    <span className="leading-snug">{m}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* ── Certificate ── */}
          {(p.certificate_details || p.certificate_type || p.assessment_details) && (
            <div className="mt-8 rounded-2xl bg-emerald-50 border border-emerald-100 p-5">
              <div className="flex items-center gap-2 mb-2">
                <Award className="w-4 h-4 text-emerald-600 shrink-0" />
                <h3 className="font-semibold text-emerald-900 text-sm">
                  {p.certificate_type ? `Certificate of ${p.certificate_type}` : "Certificate"}
                </h3>
              </div>
              {p.certificate_details && (
                <p className="text-sm text-emerald-800 leading-relaxed">{p.certificate_details}</p>
              )}
              {p.assessment_details && (
                <p className="mt-2 text-sm text-emerald-700 leading-relaxed">{p.assessment_details}</p>
              )}
              {p.handouts && (
                <div className="mt-2 flex items-center gap-1.5 text-xs text-emerald-700">
                  <Package className="w-3.5 h-3.5 shrink-0" /> Includes: {p.handouts}
                </div>
              )}
            </div>
          )}

          {/* ── Schedule / Agenda ── */}
          {p.daily_schedule && (
            <div className="mt-8">
              <h3 className="font-display text-lg font-semibold mb-3 text-[#0F172A]">Agenda / Daily Schedule</h3>
              <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans leading-relaxed bg-gray-50 rounded-xl p-4 border border-gray-100 overflow-x-auto">
                {p.daily_schedule}
              </pre>
            </div>
          )}

          {/* ── Materials included ── */}
          {p.materials_included && (
            <div className="mt-8">
              <h3 className="font-display text-lg font-semibold mb-2 text-[#0F172A]">Materials Included</h3>
              <div className="rounded-xl bg-indigo-50 border border-indigo-100 p-4">
                <div className="flex items-start gap-2">
                  <FileText className="w-4 h-4 text-indigo-500 shrink-0 mt-0.5" />
                  <p className="text-sm text-indigo-800 leading-relaxed">{p.materials_included}</p>
                </div>
              </div>
            </div>
          )}

          {/* ── Eligibility ── */}
          {p.eligibility && (
            <div className="mt-8">
              <h3 className="font-display text-lg font-semibold mb-2 text-[#0F172A]">Eligibility</h3>
              <p className="text-sm text-gray-700 leading-relaxed">{p.eligibility}</p>
            </div>
          )}

          {/* ── Requirements ── */}
          {p.requirements && (
            <div className="mt-6">
              <h3 className="font-display text-lg font-semibold mb-2 text-[#0F172A]">Requirements / Prerequisites</h3>
              <p className="text-sm text-gray-700 leading-relaxed">{p.requirements}</p>
            </div>
          )}

          {/* ── Target audience ── */}
          {p.target_audience && (
            <div className="mt-6">
              <h3 className="font-display text-lg font-semibold mb-2 text-[#0F172A]">Who should attend</h3>
              <p className="text-sm text-gray-700 leading-relaxed">{p.target_audience}</p>
            </div>
          )}

          {/* ── Assessment ── */}
          {p.assessment_details && !p.certificate_details && (
            <div className="mt-8">
              <h3 className="font-display text-lg font-semibold mb-2 text-[#0F172A]">Assessment</h3>
              <p className="text-sm text-gray-700 leading-relaxed">{p.assessment_details}</p>
            </div>
          )}

          {/* ── FAQs ── */}
          {p.faqs?.length > 0 && (
            <div className="mt-10">
              <SectionTitle>Frequently Asked Questions</SectionTitle>
              <div className="space-y-2">
                {p.faqs.map((faq, i) => <FaqItem key={i} faq={faq} />)}
              </div>
            </div>
          )}

          {/* ── Programme details row ── */}
          {(p.trainer || p.start_date || p.registration_deadline) && (
            <div className="mt-8 rounded-2xl border border-gray-200 bg-gray-50 p-4 sm:p-5 grid sm:grid-cols-2 gap-3 text-sm">
              {p.trainer && (
                <div>
                  <div className="text-xs text-gray-400 font-medium mb-0.5">Trainer</div>
                  <div className="text-gray-700">{p.trainer}</div>
                </div>
              )}
              {p.start_date && (
                <div>
                  <div className="text-xs text-gray-400 font-medium mb-0.5">Training dates</div>
                  <div className="text-gray-700">
                    {p.start_date}{p.end_date && p.end_date !== p.start_date ? ` – ${p.end_date}` : ""}
                  </div>
                </div>
              )}
              {p.training_start_time && (
                <div>
                  <div className="text-xs text-gray-400 font-medium mb-0.5">Time</div>
                  <div className="text-gray-700">
                    {p.training_start_time}
                    {p.training_end_time ? ` – ${p.training_end_time}` : ""}
                    {p.timezone ? ` ${p.timezone}` : ""}
                  </div>
                </div>
              )}
              {p.registration_deadline && (
                <div>
                  <div className="text-xs text-gray-400 font-medium mb-0.5">Registration deadline</div>
                  <div className="text-gray-700">{p.registration_deadline}</div>
                </div>
              )}
              {p.venue && (
                <div>
                  <div className="text-xs text-gray-400 font-medium mb-0.5">Venue</div>
                  <div className="text-gray-700">{p.venue}</div>
                </div>
              )}
              {p.program_code && (
                <div>
                  <div className="text-xs text-gray-400 font-medium mb-0.5">Program code</div>
                  <div className="font-mono text-gray-700">{p.program_code}</div>
                </div>
              )}
            </div>
          )}

          {/* ── Mobile CTA ── */}
          <div className="mt-10 lg:hidden">
            <CtaCard p={p} />
          </div>
        </div>

        {/* ── RIGHT / STICKY SIDEBAR ── */}
        <div className="hidden lg:block lg:col-span-5 min-w-0">
          <div className="sticky top-24 space-y-4">
            {/* Cover image — desktop */}
            {coverSrc && (
              <div className="rounded-2xl overflow-hidden aspect-[16/10] bg-gray-100">
                <img
                  src={coverSrc}
                  alt={p.title}
                  loading="lazy"
                  className="w-full h-full object-cover"
                  style={imgStyle}
                />
              </div>
            )}
            <CtaCard p={p} />
          </div>
        </div>
      </div>
    </div>
  );
}
